import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { useDropzone, type FileRejection } from "react-dropzone"
import { toast } from "sonner"
import { apiClient } from "@/api/client"
import { formatApiError } from "@/utils/httpErrors"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { PortalBrandHeader } from "@/components/PortalBrandHeader"
import { TaxConsentGate } from "@/components/TaxConsentGate"
import { PortalTermsGate } from "@/components/PortalTermsGate"

// ─── Types ───────────────────────────────────────────────────────────────────

interface ExecutiveSummaryInfo {
  headline: string
  body: string
  generated_at: string
}

interface DealInfo {
  deal_id: string
  business_name: string
  engagement_type: string
  advisor_name: string
  executive_summary?: ExecutiveSummaryInfo
}

interface FileRecord {
  doc_id: string
  filename: string
  size: number
  category: string
  uploaded_at: string
}

interface CategoryDef {
  key: string
  label: string
  description: string
  required: boolean
  accept: Record<string, string[]>
}

// ─── Constants ────────────────────────────────────────────────────────────────

// DOC-INTAKE-1: the request list is served by the backend
// (GET /portal/seller/document-requests), resolved from the catalogue in
// core/qoe/document_requests.py against the deal's industry vertical and the
// CPA's per-deal override. FALLBACK_CATEGORIES is used only if that call
// fails, so the seller still sees the universally-required core set rather
// than an empty page.
const FALLBACK_CATEGORIES: CategoryDef[] = [
  {
    key: "tax_returns",
    label: "Tax Returns",
    description: "Federal + state business tax returns (3 years preferred)",
    required: true,
    accept: { "application/pdf": [".pdf"] },
  },
  {
    key: "bank_statements",
    label: "Bank Statements",
    description: "12 months of business bank statements",
    required: true,
    accept: { "application/pdf": [".pdf"], "text/csv": [".csv"] },
  },
  {
    key: "profit_loss",
    label: "P&L Statements",
    description: "Profit & loss statements (monthly detail if available)",
    required: true,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "text/csv": [".csv"],
    },
  },
  {
    key: "other",
    label: "Other Documents",
    description: "Contracts, leases, AR/AP aging, equipment lists, etc.",
    required: false,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "text/csv": [".csv"],
    },
  },
]

// Matches MAX_UPLOAD_BYTES in apps/api/routers/_upload_guards.py.
const MAX_BYTES = 25 * 1024 * 1024 // 25 MB
const MAX_MB = MAX_BYTES / (1024 * 1024)

// VALIDATION-1C: word-for-word what the server says, so a file rejected here
// and the same file rejected there read identically. Which side caught it is an
// implementation detail the seller should never be able to infer — and the
// seller, unlike the CPA, has no login and nobody to ask, so the sentence they
// get is the entire support experience.
const UNSUPPORTED_TYPE = "That file type isn't supported. Upload a PDF, Excel, or CSV."
const EMPTY_FILE = "This file is empty — nothing to read."

const tooLarge = (bytes: number) =>
  `This file is ${(bytes / (1024 * 1024)).toFixed(1)} MB — the limit is ${MAX_MB} MB. ` +
  "Upload a smaller export or split it."

const isTouchDevice =
  typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0)

// ─── Magic-byte validation (client-side pre-check) ────────────────────────────

async function validateMagicBytes(file: File): Promise<boolean> {
  if (file.name.toLowerCase().endsWith(".csv")) return true
  const buf = await file.slice(0, 4).arrayBuffer()
  const bytes = new Uint8Array(buf)
  const isPdf = bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46
  const isZip = bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04
  return isPdf || isZip
}

// ─── Format helpers ───────────────────────────────────────────────────────────

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

// ─── UploadCategory sub-component ────────────────────────────────────────────

interface UploadCategoryProps {
  cat: CategoryDef
  token: string
  files: FileRecord[]
  uploading: boolean
  onUploaded: (record: FileRecord) => void
  onDeleted: (docId: string) => void
  onUploadStart: () => void
  onUploadEnd: () => void
}

function UploadCategory({
  cat,
  token,
  files,
  uploading,
  onUploaded,
  onDeleted,
  onUploadStart,
  onUploadEnd,
}: UploadCategoryProps) {
  const onDrop = async (accepted: File[]) => {
    for (const file of accepted) {
      if (file.size === 0) {
        toast.error(file.name, { description: EMPTY_FILE })
        continue
      }
      if (file.size > MAX_BYTES) {
        toast.error(file.name, { description: tooLarge(file.size) })
        continue
      }
      const valid = await validateMagicBytes(file)
      if (!valid) {
        toast.error(file.name, { description: UNSUPPORTED_TYPE })
        continue
      }
      onUploadStart()
      try {
        const form = new FormData()
        form.append("file", file)
        const res = await apiClient.post<FileRecord>(
          `/portal/seller/upload?token=${encodeURIComponent(token)}&category=${encodeURIComponent(cat.key)}`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        onUploaded(res.data)
        toast.success(`${file.name} uploaded`)
      } catch (err) {
        // formatApiError surfaces the router's written `detail` rather than
        // "413 - HTTP 413" (see utils/httpErrors.ts), so a server-side
        // rejection reads the same as the client-side ones above.
        toast.error(`${file.name} was not uploaded`, { description: formatApiError(err) })
      } finally {
        onUploadEnd()
      }
    }
  }

  /**
   * VALIDATION-1C: react-dropzone filters on `accept` and `maxSize` before
   * onDrop ever runs, and rejected files went nowhere — drop a .docx and the
   * dropzone simply blinked. Silence reads as "the portal is broken", and the
   * seller either retries the same file or emails the CPA to ask. Say why, per
   * file.
   */
  const onDropRejected = (rejections: FileRejection[]) => {
    for (const { file, errors } of rejections) {
      const codes = errors.map(e => e.code)
      const description = codes.includes("file-too-large")
        ? tooLarge(file.size)
        : codes.includes("file-too-small") // maxSize's counterpart: a 0-byte file
          ? EMPTY_FILE
          : UNSUPPORTED_TYPE
      toast.error(file.name, { description })
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: cat.accept,
    maxSize: MAX_BYTES,
    disabled: uploading,
  })

  const handleDelete = async (docId: string, filename: string) => {
    try {
      await apiClient.delete(`/portal/seller/files/${docId}?token=${encodeURIComponent(token)}`)
      onDeleted(docId)
      toast.success(`${filename} removed`)
    } catch (err) {
      toast.error(formatApiError(err))
    }
  }

  const handleDownload = async (docId: string, filename: string) => {
    try {
      const res = await apiClient.get<{ url: string }>(
        `/portal/seller/files/${docId}/download?token=${encodeURIComponent(token)}`
      )
      window.open(res.data.url, "_blank", "noopener,noreferrer")
    } catch (err) {
      toast.error(`Failed to open ${filename}: ${formatApiError(err)}`)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm font-semibold">{cat.label}</CardTitle>
          {cat.required && (
            <span className="text-[10px] font-medium text-white bg-primary rounded px-1.5 py-0.5">
              Required
            </span>
          )}
          {files.length > 0 && (
            <span className="ml-auto text-xs font-medium text-green-600">
              ✓ {files.length} file{files.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{cat.description}</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {/* Drop zone */}
        <div
          {...getRootProps()}
          className={[
            "border-2 border-dashed rounded-lg px-4 py-5 text-center cursor-pointer transition-colors",
            isDragActive
              ? "border-accent bg-accent/5"
              : "border-input hover:border-accent hover:bg-muted",
            uploading ? "opacity-50 cursor-not-allowed" : "",
          ].join(" ")}
        >
          <input {...getInputProps()} />
          <p className="text-xs text-muted-foreground">
            {isTouchDevice
              ? "Tap to select files"
              : isDragActive
                ? "Drop files here…"
                : "Drag & drop or click to browse"}
          </p>
          <p className="text-[10px] text-muted-foreground/60 mt-1">
            {Object.values(cat.accept).flat().join(", ")} · max 25 MB
          </p>
        </div>

        {/* File list */}
        {files.length > 0 && (
          <ul className="space-y-1">
            {files.map((f) => (
              <li
                key={f.doc_id}
                className="flex items-center gap-2 text-xs text-foreground bg-muted rounded px-2 py-1.5"
              >
                <button
                  onClick={() => handleDownload(f.doc_id, f.filename)}
                  className="flex-1 truncate text-left hover:underline hover:text-primary transition-colors"
                  title={f.filename}
                >
                  {f.filename}
                </button>
                <span className="text-muted-foreground shrink-0">{fmtBytes(f.size)}</span>
                <span className="text-muted-foreground shrink-0">{fmtDate(f.uploaded_at)}</span>
                <button
                  onClick={() => handleDelete(f.doc_id, f.filename)}
                  className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                  aria-label="Remove file"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Main SellerPortal component ──────────────────────────────────────────────

interface DealInfoWithTos extends DealInfo {
  tos_accepted?: boolean
}

export function SellerPortal() {
  const location = useLocation()
  const token = new URLSearchParams(location.search).get("token") ?? ""

  const [dealInfo, setDealInfo] = useState<DealInfoWithTos | null>(null)
  const [tosAccepted, setTosAccepted] = useState(false)
  const [tokenError, setTokenError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // DOC-INTAKE-1: request list resolved server-side (industry + CPA override)
  const [categories, setCategories] = useState<CategoryDef[]>(FALLBACK_CATEGORIES)

  // files: { [category]: FileRecord[] }
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, FileRecord[]>>({})
  const [uploading, setUploading] = useState<Record<string, boolean>>({})
  const [declaredBankAccountCount, setDeclaredBankAccountCount] = useState<string | null>(null)
  const [savingBankCount, setSavingBankCount] = useState(false)

  // Track mount to avoid state updates after unmount
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // Load deal info + existing files on mount
  useEffect(() => {
    if (!token) {
      setTokenError("No access token found. Please use the link sent by your advisor.")
      setLoading(false)
      return
    }

    const load = async () => {
      try {
        const infoRes = await apiClient.get<DealInfoWithTos>(`/portal/seller?token=${encodeURIComponent(token)}`)
        if (!mountedRef.current) return
        setDealInfo(infoRes.data)
        setTosAccepted(infoRes.data.tos_accepted ?? false)

        // DOC-INTAKE-1: resolve the request list server-side (catalogue default
        // -> industry overlay -> CPA per-deal override). Fall back to the core
        // set if the call fails, so the seller never sees an empty page.
        let cats = FALLBACK_CATEGORIES
        try {
          const reqRes = await apiClient.get<{ requests: CategoryDef[] }>(
            `/portal/seller/document-requests?token=${encodeURIComponent(token)}`
          )
          if (Array.isArray(reqRes.data.requests) && reqRes.data.requests.length > 0) {
            cats = reqRes.data.requests
          }
        } catch {
          // keep FALLBACK_CATEGORIES
        }
        if (!mountedRef.current) return
        setCategories(cats)

        // Load files for all categories
        const fileMap: Record<string, FileRecord[]> = {}
        await Promise.all(
          cats.map(async (cat) => {
            try {
              const r = await apiClient.get<{ files: FileRecord[] }>(
                `/portal/seller/files?token=${encodeURIComponent(token)}&category=${encodeURIComponent(cat.key)}`
              )
              fileMap[cat.key] = r.data.files
            } catch {
              fileMap[cat.key] = []
            }
          })
        )
        if (mountedRef.current) setUploadedFiles(fileMap)
      } catch (err: unknown) {
        if (!mountedRef.current) return
        const status = (err as { response?: { status?: number } }).response?.status
        if (status === 401) {
          setTokenError("This link is invalid or has expired. Please contact your advisor for a new link.")
        } else {
          setTokenError(formatApiError(err))
        }
      } finally {
        if (mountedRef.current) setLoading(false)
      }
    }

    load()
  }, [token])

  // Progress: count of required categories with at least 1 file
  const requiredCategories = categories.filter((c) => c.required)
  const requiredTotal = requiredCategories.length
  const completedRequired = requiredCategories.filter(
    (c) => (uploadedFiles[c.key]?.length ?? 0) > 0
  ).length
  const progressPct = requiredTotal === 0 ? 100 : Math.round((completedRequired / requiredTotal) * 100)
  const allRequiredDone = completedRequired === requiredTotal

  const handleUploaded = (category: string, record: FileRecord) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [category]: [...(prev[category] ?? []), record],
    }))
  }

  const handleDeleted = (category: string, docId: string) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [category]: (prev[category] ?? []).filter((f) => f.doc_id !== docId),
    }))
  }

  const handleBankCountChange = async (count: string) => {
    setDeclaredBankAccountCount(count)
    setSavingBankCount(true)
    try {
      await apiClient.patch(`/portal/seller/engagement?token=${encodeURIComponent(token)}`, {
        declared_bank_account_count: parseInt(count, 10) || (count === "5+" ? 5 : null),
      })
      toast.success("Bank account count saved")
    } catch (err) {
      toast.error(formatApiError(err))
      setDeclaredBankAccountCount(null)
    } finally {
      setSavingBankCount(false)
    }
  }

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PortalBrandHeader label="Secure Document Portal" />
        <div className="flex items-center justify-center py-24">
          <p className="text-sm text-muted-foreground">Loading your document portal…</p>
        </div>
      </div>
    )
  }

  // ── Invalid token ──
  if (tokenError) {
    return (
      <div className="min-h-screen bg-background">
        <PortalBrandHeader label="Secure Document Portal" />
        <div className="flex items-center justify-center p-6">
          <Card className="max-w-md w-full text-center mt-16">
            <CardContent className="pt-8 pb-8 space-y-3">
              <div className="text-4xl">🔒</div>
              <h2 className="text-lg font-semibold">Access Denied</h2>
              <p className="text-sm text-muted-foreground">{tokenError}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // ── ToS gate (if not accepted) ──
  if (!tosAccepted) {
    return (
      <div className="min-h-screen bg-background">
        <PortalBrandHeader label="Secure Document Portal" />
        <PortalTermsGate
          portalToken={token}
          role="seller"
          onAcceptTos={() => setTosAccepted(true)}
        />
      </div>
    )
  }

  // ── Authenticated portal ──
  return (
    <div className="min-h-screen bg-background">
      <PortalBrandHeader label="Secure Document Portal" />
      {/* Header bar */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold">
              {dealInfo?.business_name ?? "Document Portal"}
            </h1>
            <p className="text-xs text-muted-foreground">
              Advisor: {dealInfo?.advisor_name ?? "—"} ·{" "}
              {dealInfo?.engagement_type === "buy_side" ? "Buy-side" : "Sell-side"} engagement
            </p>
          </div>
          {allRequiredDone && (
            <span className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
              ✓ All required documents received
            </span>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {dealInfo?.executive_summary && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{dealInfo.executive_summary.headline}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-foreground/80 whitespace-pre-wrap">
              {dealInfo.executive_summary.body}
            </CardContent>
          </Card>
        )}

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Required documents uploaded</span>
            <span>{completedRequired} / {requiredTotal}</span>
          </div>
          <Progress value={progressPct} />
          {/* QOE-TRUTH-AND-JOURNEY-1 R15: the bar says how far along the
              seller is but never what remains or what happens once they
              finish — the two things a seller actually wants to know.
              No "notified" claim here — there is no push notification to
              the CPA on completion (checked: no such trigger exists), so
              this says only what's true: the documents are visible, not
              that anyone was pinged. */}
          <p className="text-xs text-muted-foreground">
            {allRequiredDone
              ? "All required documents are in. Your CPA will begin the analysis."
              : `${completedRequired} of ${requiredTotal} provided. When all ${requiredTotal} are in, your CPA begins the analysis.`}
          </p>
        </div>

        {/* Introduction */}
        <p className="text-sm text-foreground/80">
          Please upload the documents requested by your advisor. Files must be PDF, Excel (.xlsx), or CSV format
          and under 25 MB each. All uploads are encrypted in transit.
        </p>

        {/* Bank Account Count Question */}
        <Card>
          <CardContent className="pt-6">
            <label className="block text-sm font-medium mb-4">
              How many business bank accounts do you have? <span className="text-destructive">*</span>
            </label>
            <div className="space-y-2">
              {["1", "2", "3", "4", "5+"].map((count) => (
                <label key={count} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="bank_account_count"
                    value={count}
                    checked={declaredBankAccountCount === count}
                    onChange={() => handleBankCountChange(count)}
                    disabled={savingBankCount}
                    className="w-4 h-4 cursor-pointer accent-primary"
                  />
                  <span className="text-sm">{count} account{count === "1" ? "" : "s"}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* HIPAA advisory — collapsed by default so it doesn't alarm the typical
            (non-medical) seller. Many sellers are medical/dental/veterinary
            practices whose financial records may contain PHI; this platform
            is not a HIPAA-covered system and is not the place for PHI. */}
        <details className="border border-amber-200 bg-amber-50 rounded-lg px-4 py-2.5 text-sm">
          <summary className="cursor-pointer font-medium text-amber-900">
            Important: Medical Practice Clients — Read Before Uploading
          </summary>
          <div className="mt-2 text-amber-900 space-y-2 text-xs leading-relaxed">
            <p>
              <strong>If your business is a medical, dental, veterinary, or other healthcare
              practice, please read this before uploading.</strong>
            </p>
            <p>
              This platform is intended for <strong>financial</strong> documents only. Do{" "}
              <strong>not</strong> upload any document that contains <strong>protected health
              information (PHI)</strong> — for example, patient names tied to treatments, medical
              record numbers, diagnosis or procedure detail, or insurance claim data identifying
              patients.
            </p>
            <p>
              Before uploading, please <strong>redact or remove patient-identifying
              information</strong>. Financial records (profit-and-loss statements, bank statements,
              tax returns, payroll summaries, accounts-receivable aging by payer category) are what
              we need and rarely require PHI.
            </p>
            <p>
              This platform is <strong>not a HIPAA-covered system</strong> and should not be used to
              transmit PHI. If you are unsure whether a document contains PHI, contact your advisor
              before uploading it.
            </p>
          </div>
        </details>

        {/* Category cards */}
        <div className="space-y-4">
          {categories.map((cat) => {
            const card = (
              <UploadCategory
                key={cat.key}
                cat={cat}
                token={token}
                files={uploadedFiles[cat.key] ?? []}
                uploading={uploading[cat.key] ?? false}
                onUploaded={(rec) => handleUploaded(cat.key, rec)}
                onDeleted={(id) => handleDeleted(cat.key, id)}
                onUploadStart={() => setUploading((prev) => ({ ...prev, [cat.key]: true }))}
                onUploadEnd={() => setUploading((prev) => ({ ...prev, [cat.key]: false }))}
              />
            )
            if (cat.key === "tax_returns") {
              return (
                <TaxConsentGate key={cat.key} engagementId={dealInfo?.deal_id ?? ""}>
                  {card}
                </TaxConsentGate>
              )
            }
            return card
          })}
        </div>

        <p className="text-[11px] text-muted-foreground text-center">
          Questions? Contact your advisor directly. Do not share this link with others.
        </p>
        <p className="text-[11px] text-muted-foreground text-center">
          {/* R5 (2026-08-24): the platform's own "Request Custom Branding"
              mailto link was removed from this client-facing surface. */}
          <a href="/privacy-policy" className="underline hover:text-primary transition-colors">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  )
}
